'use client'
import { Image } from '@/types/common/ImageData'
import s from './Teams.module.scss'
import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import React from 'react'
import LazyImage from '@/components/common/LazyImage'
import { PortableText } from 'next-sanity'
import { portableBlockComponents } from '@/utils/portableText'
import { Body } from '@/types/common/Body'

interface TeamsProps {
    data: DataProps
}

interface ImageProp {
    image: Image;
}

interface MemberProps {
    name: string;
    surname: string;
    position: string;
    email: string | null;
    description: Body[] | null;
    featuredImage: ImageProp | null;
    bio: boolean | null;
}

interface DataProps {
    _type: string;
    _ref: string;
    title: string | null;
    members: Array<MemberProps> | null;
    orderByType: string | null;
}

export default function Teams({ data }: TeamsProps) {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [clickedIndex, setClickedIndex] = useState<number | null>(null);
    const [isMobile, setIsMobile] = useState(false);

    // Detectar si es mobile o desktop
    useEffect(() => {
        const checkScreenSize = () => setIsMobile(window.innerWidth < 768);
        checkScreenSize();
        window.addEventListener("resize", checkScreenSize);

        return () => window.removeEventListener("resize", checkScreenSize);
    }, []);

    useEffect(() => {
        if (isMobile && clickedIndex === null) {
            setHoveredIndex(null);
        }
    }, [isMobile, clickedIndex]);


    const sortedMembers = useMemo(() => {
        if (!data.members) return [];

        switch (data.orderByType) {
            case 'name':
                return [...data.members].sort((a, b) => a.name.localeCompare(b.name));
            case 'surname':
                return [...data.members].sort((a, b) => a.surname.localeCompare(b.surname));
            case 'manual':
            default:
                return data.members; // Keep original order
        }
    }, [data.members, data.orderByType]);

    return (
        <div className={s.teams}>
            {data.title && <p className={s.title}>{data.title}</p>}
            <div className={s.members}>
                {sortedMembers.map((member: MemberProps, index: number) => {
                    const isOpen = clickedIndex === index;;
                    if (member.bio) {
                        return (
                            <div
                                key={index}
                                className={s.bioMember}
                                onMouseEnter={() => setHoveredIndex(index)}
                                onMouseLeave={() => setHoveredIndex(null)}
                            >
                                <div
                                    className={s.titleMember + ' ' + (hoveredIndex === index ? s.active : hoveredIndex === null ? '' : s.inactive) + ' ' + (clickedIndex === index ? s.clicked : clickedIndex === null ? '' : s.inactive)}
                                    onClick={() => setClickedIndex(isOpen ? null : index)}
                                >
                                    <p>{member.name} {member.surname} <span className={`${s.gray} ${s.position}`}>{member.position}</span></p>
                                    <div className={s.plusMinus + ' ' + (clickedIndex === index ? s.active : '')}>
                                        <svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <line x1="12" y1="0.893433" x2="12" y2="22.1066" stroke="#0A0A0A" />
                                            <line x1="22.1094" y1="12" x2="0.896172" y2="12" stroke="#0A0A0A" />
                                        </svg>
                                    </div>
                                </div>
                                <div className={`${s.bioMemberContent} ${clickedIndex === index ? s.active : ''}`}>
                                    <div className={s.bioMemberContentIn}>
                                        <div className={s.bioMemberText}>
                                            {member.description &&
                                                <div className={s.description}>
                                                    <PortableText
                                                        value={member.description}
                                                        components={portableBlockComponents(member.description)}
                                                    />
                                                </div>
                                            }
                                            <div className={s.email}>
                                                {member.email && <p><Link href={`mailto:${member.email}`}>{member.email}</Link></p>}
                                            </div>
                                        </div>
                                        {member.featuredImage && member.featuredImage.image && (
                                            <div className={s.bioMemberImage}>
                                                <LazyImage
                                                    src={member.featuredImage.image.imageUrl}
                                                    width={member.featuredImage.image.metadata.dimensions.width}
                                                    height={member.featuredImage.image.metadata.dimensions.height}
                                                    blurDataURL={member.featuredImage.image.ref}
                                                    fill={true}
                                                    alt={member.featuredImage.image.attribution || ""}
                                                    caption={member.featuredImage.image.caption || null}
                                                    filename={member.featuredImage.image.filename || ""}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    } else {
                        return (
                            <div key={index} className={s.nonBioMember + ' ' + (hoveredIndex === index ? s.active : hoveredIndex === null ? '' : s.inactive)}>
                                <p>{member.name} {member.surname} <span className={s.gray}>{member.position}</span></p>
                            </div>
                        );
                    }
                })}
            </div>
        </div>
    )
}